import React, { useEffect, useState, useCallback } from 'react';
import { ConsentCategories, hasConsent } from './CookieConsent';
import '../../styles/ConsentEmbed.css';

type ConsentEmbedProps = {
  category: keyof ConsentCategories;
  providerName: string;
  description?: string;
  privacyPolicyUrl?: string;
  thumbnail?: string;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
};

const ConsentEmbed: React.FC<ConsentEmbedProps> = ({
  category,
  providerName,
  description,
  privacyPolicyUrl,
  thumbnail,
  ariaLabel,
  className,
  children,
}) => {
  const [granted, setGranted] = useState<boolean>(() => hasConsent(category));

  useEffect(() => {
    const handleConsentChange = () => {
      setGranted(hasConsent(category));
    };
    window.addEventListener('consent-change', handleConsentChange);
    return () => window.removeEventListener('consent-change', handleConsentChange);
  }, [category]);

  const openCookieSettings = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-cookie-settings'));
  }, []);

  if (granted) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={`consent-embed ${className ?? ''}`}
      role="region"
      aria-label={ariaLabel ?? `Externer Inhalt von ${providerName}`}
      style={
        thumbnail
          ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${thumbnail})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      <div className="consent-embed-content">
        <h3 className="consent-embed-title">Externer Inhalt: {providerName}</h3>
        <p className="consent-embed-text">
          {description ??
            `An dieser Stelle wird ein Inhalt von ${providerName} eingebettet. Beim Laden werden personenbezogene Daten (insbesondere Ihre IP-Adresse) an den Anbieter übertragen.`}
        </p>
        <p className="consent-embed-text consent-embed-meta">
          Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG.
          {privacyPolicyUrl && (
            <>
              {' '}
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="consent-embed-link"
              >
                Datenschutz des Anbieters
              </a>
              .
            </>
          )}
        </p>
        <div className="consent-embed-actions">
          <button
            type="button"
            className="consent-embed-btn consent-embed-btn-primary"
            onClick={openCookieSettings}
          >
            Einwilligung erteilen
          </button>
          <a
            href="/datenschutz"
            className="consent-embed-link consent-embed-link-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mehr erfahren
          </a>
        </div>
      </div>
    </div>
  );
};

export default ConsentEmbed;
